<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, nextTick, onUnmounted, ref, watch } from "vue"
import { onClickOutside, useEventBus } from "@vueuse/core"
import { useI18n } from "vue-i18n"
import { useRoute, useRouter } from "vue-router"
import { useAuthStore } from "@core/stores/auth.store"
import { LAYOUT_SIDEBAR_EXPANDED_EVENT } from "@core/layouts/layout.events"
import { SUPPORT_OPEN_EVENT } from "./support.events"
import { useResponsive } from "@core/composables"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            support: {
                buttonLabel: "Aide",
                menuLabel: "Aide",
                comprendre: "Comprendre l'étude",
                ecrire: "Écrire à l'aide",
            },
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================
type Props = {
    /**
     * Masque le bouton tout en gardant la modal disponible (ex. ouverture programmatique).
     */
    hideButton?: boolean
}

// ================================
// PROPS & EMITS
// ================================
withDefaults(defineProps<Props>(), {
    hideButton: false,
})

// ================================
// COMPOSABLES
// ================================
const authStore = useAuthStore()
const { isMobile } = useResponsive()
const route = useRoute()
const router = useRouter()

// ================================
// STATE
// ================================
/**
 * Contrôle l'affichage de la modal d'aide.
 */
const visible = ref(false)

/**
 * Sidebar layout développée : z-index du bouton sous le calque `z-1` de l’aside.
 */
const isSidebarExpanded = ref(false)

/**
 * DEV-2311, décision du 07/10 (pistes v0.4, carte 2, A2) : sur les pages de
 * l'Optimisation du TURPE (l'étude et sa page d'aide), le bouton « Aide » ouvre
 * un petit menu à deux entrées, lire (la page « Comprendre l'étude ») ou écrire
 * (la fenêtre d'aide). Ailleurs, un clic ouvre la fenêtre, comme avant.
 */
const ROUTES_MENU_OPTIMTURPE = new Set([
    "PageBuildingOptimTurpeV2",
    "PageBuildingOptimTurpeV2Aide",
])
const ROUTE_AIDE_OPTIMTURPE = "PageBuildingOptimTurpeV2Aide"

/** Le menu lire ou écrire est ouvert. */
const isMenuOpen = ref(false)
const rootRef = ref<HTMLElement | null>(null)
const menuRef = ref<HTMLElement | null>(null)

// ================================
// COMPUTED
// ================================
/**
 * Indique si le bouton d'aide doit être affiché (employé avec droits d'écriture et client Homeys).
 */
const getCanShowButton = computed(() => {
    return authStore.isEmployeWriteOrHigher && authStore.isClientHomeys
})

/** La page courante propose le menu lire ou écrire. */
const getHasMenu = computed(() =>
    ROUTES_MENU_OPTIMTURPE.has(String(route.name ?? ""))
)

// ================================
// INTERACTIONS
// ================================
/** Les entrées focalisables du menu, dans l'ordre. */
function menuItems(): HTMLElement[] {
    return Array.from(
        menuRef.value?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []
    )
}

/** Ferme le menu ; `rendreLeFocus` ramène le focus sur le bouton (Échap). */
function closeMenu(rendreLeFocus = false): void {
    if (!isMenuOpen.value) return
    isMenuOpen.value = false
    if (rendreLeFocus) {
        rootRef.value
            ?.querySelector<HTMLElement>('[data-testid="support-button"]')
            ?.focus()
    }
}

/**
 * Au clic sur le bouton : sur les pages de l'Optimisation du TURPE, ouvre (ou
 * referme) le menu ; ailleurs, ouvre la modal d'aide.
 */
async function onClickOpenSupport(): Promise<void> {
    if (!getHasMenu.value) {
        visible.value = true
        return
    }
    isMenuOpen.value = !isMenuOpen.value
    if (isMenuOpen.value) {
        await nextTick()
        menuItems()[0]?.focus()
    }
}

/**
 * « Comprendre l'étude » : la page d'aide, sur le bâtiment et le compteur
 * courants ; sans effet si l'on y est déjà.
 */
function onClickComprendre(): void {
    closeMenu()
    if (route.name === ROUTE_AIDE_OPTIMTURPE) return
    const idsource = route.query.idsource
    router.push({
        name: ROUTE_AIDE_OPTIMTURPE,
        params: { _id_building: String(route.params._id_building ?? "") },
        query: typeof idsource === "string" && idsource ? { idsource } : {},
    })
}

/** « Écrire à l'aide » : la fenêtre d'aide, par l'événement habituel. */
function onClickEcrire(): void {
    closeMenu()
    busSupportOpen.emit()
}

/** Clavier dans le menu : Échap ferme, flèches et Début/Fin déplacent le focus. */
function onMenuKeydown(event: KeyboardEvent): void {
    const items = menuItems()
    const i = items.indexOf(document.activeElement as HTMLElement)
    const aller = (n: number) => {
        event.preventDefault()
        items[(n + items.length) % items.length]?.focus()
    }
    if (event.key === "Escape") {
        event.preventDefault()
        closeMenu(true)
    } else if (event.key === "ArrowDown") aller(i + 1)
    else if (event.key === "ArrowUp") aller(i - 1)
    else if (event.key === "Home") aller(0)
    else if (event.key === "End") aller(items.length - 1)
    else if (event.key === "Tab") closeMenu()
}

onClickOutside(rootRef, () => closeMenu())
watch(
    () => route.fullPath,
    () => closeMenu()
)

// ================================
// LIFECYCLE
// ================================
const busSupportOpen = useEventBus<void>(SUPPORT_OPEN_EVENT)
const busSidebarExpanded = useEventBus<boolean>(LAYOUT_SIDEBAR_EXPANDED_EVENT)

const stopSupportOpenListener = busSupportOpen.on(() => {
    visible.value = true
})
const stopSidebarExpandedListener = busSidebarExpanded.on((expanded) => {
    isSidebarExpanded.value = expanded
})

onUnmounted(() => {
    stopSupportOpenListener()
    stopSidebarExpandedListener()
})
</script>

<template>
    <!-- ================================ -->
    <!-- COMPOSANT PRINCIPAL -->
    <!-- ================================ -->
    <div
        ref="rootRef"
        class="support-button fixed bottom-8 right-8"
        :class="isSidebarExpanded && isMobile ? 'z-0' : 'z-[2000]'"
        @keydown.esc="closeMenu(true)"
    >
        <!-- Menu lire ou écrire (pages de l'Optimisation du TURPE), au-dessus
             du bouton, aligné sur son bord droit : il reste dans l'écran. -->
        <div
            v-if="getHasMenu && isMenuOpen && !hideButton && getCanShowButton"
            id="support-button-menu"
            ref="menuRef"
            role="menu"
            :aria-label="t('support.menuLabel')"
            class="absolute bottom-full right-0 mb-2 flex w-max max-w-[calc(100vw-4rem)] flex-col rounded-md border border-surface-200 bg-white py-1 shadow-lg"
            data-testid="support-button-menu"
            @keydown="onMenuKeydown"
        >
            <button
                type="button"
                role="menuitem"
                class="flex items-center gap-2 px-4 py-2 text-left text-sm text-surface-700 hover:bg-primary-50 hover:text-primary-700 focus:bg-primary-50 focus:outline-none"
                data-testid="support-button-menu-comprendre"
                @click="onClickComprendre"
            >
                <i class="pi pi-book text-sm" aria-hidden="true" />
                <span>{{ t("support.comprendre") }}</span>
            </button>
            <button
                type="button"
                role="menuitem"
                class="flex items-center gap-2 px-4 py-2 text-left text-sm text-surface-700 hover:bg-primary-50 hover:text-primary-700 focus:bg-primary-50 focus:outline-none"
                data-testid="support-button-menu-ecrire"
                @click="onClickEcrire"
            >
                <i class="pi pi-envelope text-sm" aria-hidden="true" />
                <span>{{ t("support.ecrire") }}</span>
            </button>
        </div>
        <BaseButton
            v-if="!hideButton && getCanShowButton"
            icon-pos="left"
            :label="t('support.buttonLabel')"
            rounded
            data-testid="support-button"
            :aria-haspopup="getHasMenu ? 'menu' : undefined"
            :aria-expanded="getHasMenu ? String(isMenuOpen) : undefined"
            :aria-controls="
                getHasMenu && isMenuOpen ? 'support-button-menu' : undefined
            "
            @click="onClickOpenSupport"
        >
            <template #icon>
                <BaseIcon icon="far fa-circle-question" class="text-base" />
            </template>
        </BaseButton>
        <SupportModal v-model:visible="visible" />
    </div>
</template>
